import axios, { type AxiosRequestConfig, type InternalAxiosRequestConfig } from 'axios'
import type { ApiItem, ApiPage } from '@/types/api'
import i18n from '@/i18n'
import { toApiError } from './errors'

const baseURL = (import.meta.env.VITE_API_URL as string | undefined) ?? '/api/v1'

/**
 * Access tokens live only in memory. The refresh token is an HttpOnly cookie the browser sends to
 * /api/v1/auth/refresh, so a page reload silently re-establishes the session.
 */
let accessToken: string | null = null
let refreshHandler: (() => Promise<string | null>) | null = null
let refreshInFlight: Promise<string | null> | null = null

export function setAccessToken(token: string | null): void {
  accessToken = token
}

export function getAccessToken(): string | null {
  return accessToken
}

/** Registered by the auth provider; returns a fresh access token or null when the session is gone. */
export function setRefreshHandler(handler: (() => Promise<string | null>) | null): void {
  refreshHandler = handler
}

export const http = axios.create({
  baseURL,
  withCredentials: true,
  headers: { Accept: 'application/json' },
  timeout: 30_000,
})

http.interceptors.request.use((config) => {
  if (accessToken) config.headers.set('Authorization', `Bearer ${accessToken}`)
  // The API localises its messages from this header (French by default).
  config.headers.set('Accept-Language', i18n.language)
  return config
})

type RetriableConfig = InternalAxiosRequestConfig & { _retried?: boolean }

http.interceptors.response.use(
  (response) => response,
  async (error: unknown) => {
    const apiError = toApiError(error)
    const config = axios.isAxiosError(error) ? (error.config as RetriableConfig | undefined) : undefined
    const isAuthCall = config?.url?.startsWith('/auth/') ?? false

    if (apiError.status === 401 && config && !config._retried && !isAuthCall && refreshHandler) {
      config._retried = true
      // Single-flight: concurrent 401s share one refresh request.
      refreshInFlight ??= refreshHandler().finally(() => {
        refreshInFlight = null
      })
      const token = await refreshInFlight
      if (token) {
        config.headers.set('Authorization', `Bearer ${token}`)
        return http.request(config)
      }
    }
    throw apiError
  },
)

/** Typed helpers that unwrap the { data } envelope. */
export const api = {
  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const res = await http.get<ApiItem<T>>(url, config)
    return res.data.data
  },
  async page<T>(url: string, params?: object): Promise<ApiPage<T>> {
    const res = await http.get<ApiPage<T>>(url, { params: cleanParams(params) })
    return res.data
  },
  async post<T>(url: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const res = await http.post<ApiItem<T>>(url, body, config)
    return res.data.data
  },
  async put<T>(url: string, body?: unknown): Promise<T> {
    const res = await http.put<ApiItem<T>>(url, body)
    return res.data.data
  },
  async patch<T>(url: string, body?: unknown): Promise<T> {
    const res = await http.patch<ApiItem<T>>(url, body)
    return res.data.data
  },
  async delete(url: string): Promise<void> {
    await http.delete(url)
  },
}

/** Drops empty filter values so query strings stay clean and cache keys stable. */
export function cleanParams(params?: object): Record<string, string | number | boolean> | undefined {
  if (!params) return undefined
  const out: Record<string, string | number | boolean> = {}
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') out[key] = value
  }
  return out
}
